import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Save, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIET_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'halal', label: 'Halal' },
  { value: 'gluten_free', label: 'Gluten Free' }
];

export default function BulkTagEditor({ user, onLogout }) {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [expandedRecipes, setExpandedRecipes] = useState({});
  const [suggestions, setSuggestions] = useState({});

  useEffect(() => {
    fetchRecipes();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = recipes.filter(r => 
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRecipes(filtered);
    } else {
      setFilteredRecipes(recipes);
    }
  }, [searchTerm, recipes]);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/library-recipes`);
      setRecipes(response.data);
      setFilteredRecipes(response.data);
    } catch (error) {
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (recipeId, tag) => {
    const recipe = recipes.find(r => r.id === recipeId);
    const currentTags = changes[recipeId] || recipe.dietary_categories || [];
    
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    setChanges({ ...changes, [recipeId]: newTags });
  };

  const getCurrentTags = (recipeId) => {
    if (changes[recipeId]) return changes[recipeId];
    const recipe = recipes.find(r => r.id === recipeId);
    return recipe?.dietary_categories || [];
  };

  const toggleExpand = (recipeId) => {
    setExpandedRecipes(prev => ({
      ...prev,
      [recipeId]: !prev[recipeId]
    }));
  };

  // Exact exclusion lists from user's diet type table (updated)
  const VEGETARIAN_EXCLUSIONS = ['Chicken Breast', 'Chicken Thigh(with skin)', 'Ground Chicken', 'Ground Beef 93/7', 'Ground Beef 80/20', 'Sirloin Steak', 'Salmon', 'Tuna', 'Tilapia', 'Cod', 'Turkey Breast', 'Ground Turkey', 'Duck Breast', 'Ribeye Steak', 'Flank Steak', 'Pork Chop', 'Pork Tenderloin', 'Ground Pork', 'Bacon', 'Lamb Chop', 'Ground Lamb', 'Mahi Mahi', 'Halibut', 'Trout', 'Shrimp', 'Scallops', 'Crab', 'Lobster', 'Ham Deli Meat', 'Turkey Deli Meat', 'Roast Beef Deli', 'Sausage Italian', 'Chorizo', 'Pepperoni', 'Salami', 'Tuna Canned in Water', 'Salmon Canned', 'Sardines Canned', 'Anchovies', 'Sardines', 'Pork Mince', 'Pork Belly', 'Duck', 'Mackerel', 'Beef Ribeye', 'Beef Sirloin', 'Beef Flank', 'Lamb Mince', 'Lamb Leg', 'Lean Steak Strips', 'Pork Loin', 'Ham Slices', 'Sausage', 'Salmon Fillet', 'Cod Fillet', 'Tuna (Fresh)', 'Sardines (Fresh)', 'Prawns', 'Beef Meatballs', 'Turkey Meatballs', 'Canned Mackerel', 'Canned Haddock', 'Cooked Prawns', 'Rotisserie Chicken', 'Canned Chicken Breast', 'Turkey Bacon', 'Chicken Sausage', 'Smoked Salmon', 'Chicken Thigh', 'Chicken Drumsticks', 'Chicken Mince', 'Turkey Mince', 'Beef Mince 90/10', 'Beef Mince 80/20', 'Beef Mince 93/7', 'Bacon Fat', 'Fish Sauce', 'Worcestershire Sauce'];
  
  const VEGAN_EXCLUSIONS = ['Chicken Breast', 'Chicken Thigh(with skin)', 'Ground Chicken', 'Ground Beef 93/7', 'Ground Beef 80/20', 'Sirloin Steak', 'Salmon', 'Tuna', 'Tilapia', 'Cod', 'Turkey Breast', 'Ground Turkey', 'Duck Breast', 'Ribeye Steak', 'Flank Steak', 'Pork Chop', 'Pork Tenderloin', 'Ground Pork', 'Bacon', 'Lamb Chop', 'Ground Lamb', 'Mahi Mahi', 'Halibut', 'Trout', 'Shrimp', 'Scallops', 'Crab', 'Lobster', 'Ham Deli Meat', 'Turkey Deli Meat', 'Roast Beef Deli', 'Sausage Italian', 'Chorizo', 'Pepperoni', 'Salami', 'Tuna Canned in Water', 'Salmon Canned', 'Sardines Canned', 'Anchovies', 'Sardines', 'Pork Mince', 'Pork Belly', 'Duck', 'Mackerel', 'Beef Ribeye', 'Beef Sirloin', 'Beef Flank', 'Lamb Mince', 'Lamb Leg', 'Lean Steak Strips', 'Pork Loin', 'Ham Slices', 'Sausage', 'Salmon Fillet', 'Cod Fillet', 'Tuna (Fresh)', 'Sardines (Fresh)', 'Prawns', 'Beef Meatballs', 'Turkey Meatballs', 'Canned Mackerel', 'Canned Haddock', 'Cooked Prawns', 'Rotisserie Chicken', 'Canned Chicken Breast', 'Turkey Bacon', 'Chicken Sausage', 'Smoked Salmon', 'Chicken Thigh', 'Chicken Drumsticks', 'Chicken Mince', 'Turkey Mince', 'Beef Mince 90/10', 'Beef Mince 80/20', 'Beef Mince 93/7', 'Bacon Fat', 'Fish Sauce', 'Worcestershire Sauce', 'Whole Egg', 'Egg White', 'Egg Yolk', 'Egg', 'Greek Yogurt Plain', 'Cottage Cheese', 'Milk Whole', 'Milk 2%', 'Milk Skim', 'Greek Yogurt Nonfat', 'Cottage Cheese Low Fat', 'Cheddar Cheese', 'Mozzarella Cheese', 'Feta Cheese', 'Parmesan Cheese', 'Sour Cream', 'Cream Cheese', 'Ricotta Cheese', 'Goat Cheese', 'Blue Cheese', 'Heavy Cream', 'Half and Half', 'Full-Fat Greek Yogurt', 'Full-Fat Cottage Cheese', 'Brie Cheese', 'Mascarpone', 'Paneer', 'Halloumi Cheese', 'Halloumi Light', 'Yogurt Strawberry', 'Ricotta Light', 'Ricotta (Reduced-Fat)', 'Light Cream Cheese', 'Soft Cheese (Low-Fat)', 'Skyr (Plain)', 'Quark (Plain)', 'Protein Yogurt', 'Protein Milk', 'Greek Yogurt (Vanilla)', 'Buttermilk', 'Kefir', 'Reduced-Fat Cheddar', 'Cheese', 'Butter Salted', 'Butter Unsalted', 'Butter', 'Ghee', 'Honey', 'Honey mustard dressing', 'Mayo', 'mayonnaise', 'chipotle mayo', 'bbq mayo sauce', 'ranch dressing', 'caesar dressing', 'tartar sauce', 'italian dressing'];
  
  const PESCATARIAN_EXCLUSIONS = ['Chicken Breast', 'Chicken Thigh(with skin)', 'Ground Chicken', 'Turkey Breast', 'Ground Turkey', 'Duck Breast', 'Pork Chop', 'Pork Tenderloin', 'Ground Pork', 'Bacon', 'Ham Deli Meat', 'Turkey Deli Meat', 'Roast Beef Deli', 'Sausage Italian', 'Chorizo', 'Pepperoni', 'Salami', 'Pork Mince', 'Pork Belly', 'Duck', 'Beef Ribeye', 'Sirloin Steak', 'Beef Sirloin', 'Beef Flank', 'Flank Steak', 'Lamb Chop', 'Ground Lamb', 'Lamb Mince', 'Lamb Leg', 'Lean Steak Strips', 'Pork Loin', 'Ham Slices', 'Sausage', 'Beef Meatballs', 'Turkey Meatballs', 'Rotisserie Chicken', 'Canned Chicken Breast', 'Turkey Bacon', 'Chicken Sausage', 'Chicken Thigh', 'Chicken Drumsticks', 'Chicken Mince', 'Turkey Mince', 'Beef Mince 90/10', 'Beef Mince 80/20', 'Beef Mince 93/7', 'Bacon Fat'];
  
  const HALAL_EXCLUSIONS = ['Pork Chop', 'Pork Tenderloin', 'Ground Pork', 'Pork Mince', 'Pork Belly', 'Pork Loin', 'Bacon', 'Bacon Fat', 'Ham Deli Meat', 'Ham Slices'];
  
  const GLUTEN_FREE_EXCLUSIONS = ['Pasta (Uncooked)', 'Whole Wheat Pasta (Uncooked)', 'Wholewheat Pasta (Uncooked)', 'Wholegrain Pasta (Uncooked)', 'White Pasta (Uncooked)', 'Couscous (Uncooked)', 'Couscous(Uncooked)', 'Barley (Uncooked)', 'Pearl Barley (Uncooked)', 'Bulgur (Uncooked)', 'Bulgur Wheat (Uncooked)', 'Farro Cooked', 'Whole Wheat Bread', 'White Bread', 'Sourdough Bread', 'Sourdough', 'Rye Bread', 'Wholemeal Bread', 'Wholemeal Pitta', 'Multigrain Bread', 'Wholegrain Toast', 'Bagel Plain', 'English Muffin', 'Crumpet', 'Croissant', 'Brioche Bread', 'Brioche Burger Bun', 'Ciabatta Roll', 'Naan Bread', 'Garlic Naan', 'Pita Bread', 'Wholegrain Wrap', 'Tortilla Flour', 'Tortilla Wrap', 'Garlic Bread', 'Breakfast Flatbread', 'Digestive Biscuits', 'Crackers Whole Wheat', 'Crackers Saltine', 'Pretzels', 'Granola', 'Muesli (Unsweetened)', 'Oats Dry', 'Oats', 'Instant Oats(Uncooked)', 'Seitan', 'Gnocchi', 'Taco Shell', 'Crispy Tortilla Chips', 'Marmite', 'Soy Sauce', 'Reduced-Salt Soy Sauce', 'sweet soy sauce', 'Teriyaki sauce', 'Hoisin sauce', 'Gochujang (korean chilli paste)', 'Brown Sauce (hp type)', 'HP Sauce', 'Sweet and sour sauce', 'Tikka masala paste', 'Korma paste', 'Worcestershire Sauce'];

  const analyzeRecipe = (recipe) => {
    const ingredients = recipe.ingredients || [];
    
    // Get all ingredient names from the recipe
    const ingredientNames = ingredients.map(ing => 
      ing.food_bank_name || ing.food_item_name || ''
    );
    
    const tags = ['standard'];

    // Check if recipe contains any excluded ingredients
    const hasVegetarianExclusion = ingredientNames.some(name => 
      VEGETARIAN_EXCLUSIONS.includes(name)
    );
    
    const hasVeganExclusion = ingredientNames.some(name => 
      VEGAN_EXCLUSIONS.includes(name)
    );
    
    const hasPescatarianExclusion = ingredientNames.some(name => 
      PESCATARIAN_EXCLUSIONS.includes(name)
    );
    
    const hasHalalExclusion = ingredientNames.some(name => 
      HALAL_EXCLUSIONS.includes(name)
    );
    
    const hasGlutenFreeExclusion = ingredientNames.some(name => 
      GLUTEN_FREE_EXCLUSIONS.includes(name)
    );

    // Apply tags based on exclusions
    if (!hasVegetarianExclusion) {
      tags.push('vegetarian');
    }
    
    if (!hasVeganExclusion) {
      tags.push('vegan');
    }
    
    if (!hasPescatarianExclusion) {
      tags.push('pescatarian');
    }
    
    if (!hasHalalExclusion) {
      tags.push('halal');
    }
    
    if (!hasGlutenFreeExclusion) {
      tags.push('gluten_free');
    }

    return tags;
  };

  const suggestAllTags = () => {
    const newSuggestions = {};
    const newChanges = { ...changes };

    filteredRecipes.forEach(recipe => {
      const suggestedTags = analyzeRecipe(recipe);
      newSuggestions[recipe.id] = suggestedTags;
      newChanges[recipe.id] = suggestedTags;
    });

    setSuggestions(newSuggestions);
    setChanges(newChanges);
    toast.success(`Suggested tags for ${filteredRecipes.length} recipes`);
  };

  const applySuggestion = (recipeId) => {
    if (suggestions[recipeId]) {
      setChanges({ ...changes, [recipeId]: suggestions[recipeId] });
    }
  };

  const saveAll = async () => {
    if (Object.keys(changes).length === 0) {
      toast.error('No changes to save');
      return;
    }

    setSaving(true);
    let successCount = 0;

    try {
      for (const [recipeId, tags] of Object.entries(changes)) {
        await axios.put(`${API}/library-recipes/${recipeId}/metadata`, {
          dietary_categories: tags
        });
        successCount++;
      }

      toast.success(`✅ Updated ${successCount} recipes`);
      setChanges({});
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to save some recipes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading recipes...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/recipe-library')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Library
              </Button>
              <h1 className="text-2xl font-bold">Bulk Tag Editor</h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={suggestAllTags}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                Auto-Suggest All Tags
              </Button>
              <Button
                onClick={saveAll}
                disabled={saving || Object.keys(changes).length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save All ({Object.keys(changes).length})
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <p className="text-sm text-gray-500 mt-2">
            {filteredRecipes.length} recipes • Click checkboxes to tag, then Save All
          </p>
        </div>
      </div>

      {/* Recipe Table */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-[140px] z-5">
                  <tr>
                    <th className="text-left p-3 font-semibold text-sm w-1/3">Recipe Name</th>
                    {DIET_OPTIONS.map(diet => (
                      <th key={diet.value} className="text-center p-3 font-semibold text-sm">
                        {diet.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRecipes.map((recipe, idx) => {
                    const currentTags = getCurrentTags(recipe.id);
                    const hasChanges = !!changes[recipe.id];
                    const isExpanded = expandedRecipes[recipe.id];

                    return (
                      <>
                        <tr
                          key={recipe.id}
                          className={`border-b hover:bg-gray-50 ${hasChanges ? 'bg-yellow-50' : ''} ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleExpand(recipe.id)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                              {hasChanges && (
                                <span className="text-yellow-600 text-xs">●</span>
                              )}
                              <span className="font-medium">{recipe.name}</span>
                            </div>
                          </td>
                          {DIET_OPTIONS.map(diet => (
                            <td key={diet.value} className="text-center p-3">
                              <input
                                type="checkbox"
                                checked={currentTags.includes(diet.value)}
                                onChange={() => toggleTag(recipe.id, diet.value)}
                                className="w-5 h-5 cursor-pointer"
                              />
                            </td>
                          ))}
                        </tr>
                        {isExpanded && (
                          <tr key={`${recipe.id}-expanded`} className="bg-blue-50">
                            <td colSpan={DIET_OPTIONS.length + 1} className="p-4">
                              <div className="text-sm">
                                <p className="font-semibold mb-2">Ingredients:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  {recipe.ingredients?.map((ing, i) => (
                                    <li key={i} className="text-gray-700">
                                      {ing.food_item_name || ing.food_bank_name} ({ing.display_text || `${Math.round(ing.grams)}g`})
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Floating Save Button */}
      {Object.keys(changes).length > 0 && (
        <div className="fixed bottom-4 right-4">
          <Button
            onClick={saveAll}
            disabled={saving}
            size="lg"
            className="bg-green-600 hover:bg-green-700 shadow-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            Save {Object.keys(changes).length} Changes
          </Button>
        </div>
      )}
    </div>
  );
}
